import * as React from 'react';
import { Reveal, useIsMobile } from '../anim.jsx';
import { Icon } from '../icons.jsx';
import { Button, Badge, Tag, Card, Placeholder, ProviderPhoto, Pill, Toggle } from '../ui.jsx';
import { AGE_GROUPS, AGE_GROUP_RANGES, SCHEDULES } from '../data';
import { api, hasOpeningsForAges } from '../api';
import { useApi, Skeleton, CardSkeleton, ErrorState } from '../hooks';

// Search Results page

const ResultCard = ({
  p,
  go,
  onBook,
  onWaitlist,
  delay,
  searchedAges,
  comparisonSelected,
  comparisonDisabled,
  onToggleComparison,
}) => {
  const isOpen = hasOpeningsForAges(p, searchedAges);
  const searchedAge = searchedAges?.length === 1 ? searchedAges[0] : null;
  const relevantSpots = searchedAge ? p.availability[searchedAge].spots : p.spots;
  const availabilityLabel = searchedAge
    ? `${relevantSpots} ${searchedAge} ${relevantSpots === 1 ? 'spot' : 'spots'}`
    : 'Open Spots';
  return (
    <Reveal delay={delay} style={{ display: 'block' }}>
      <Card hoverable onClick={() => go('profile', { providerId: p.id })} style={{
        opacity: isOpen ? 1 : 0.78,
        filter: isOpen ? 'none' : 'saturate(0.7)',
        height: '100%',
      }}>
        <div style={{ padding: 10, position: 'relative' }}>
          {p.image
            ? <ProviderPhoto src={p.image} alt={`${p.name} childcare space`} radius={10} height={180} label={p.neighborhood}/>
            : <Placeholder tone={p.tone} radius={10} height={180} label={p.neighborhood}/>}
          <div style={{ position: 'absolute', top: 20, left: 20 }}>
            {isOpen
              ? <Badge tone="open" icon="live">{availabilityLabel}</Badge>
              : <Badge tone="wait">Waitlist Only</Badge>}
          </div>
          {p.subsidy && (
            <div style={{ position: 'absolute', top: 20, right: 20 }}>
              <Badge tone="brand" icon="shield">Subsidy</Badge>
            </div>
          )}
        </div>
        <div style={{ padding: '6px 18px 18px' }}>
          <h3 style={{
            fontSize: 17, fontWeight: 700, color: 'var(--ink)',
            letterSpacing: '-0.01em', marginBottom: 4,
          }}>
            {p.name}
          </h3>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            color: 'var(--ink-3)', fontSize: 13,
          }}>
            <Icon name="pin" size={13}/>
            <span>{p.neighborhood} · {p.distance}</span>
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            {p.ages.map(a => <Tag key={a}>{a}</Tag>)}
          </div>

          <button
            type="button"
            className={`result-compare-toggle ${comparisonSelected ? 'is-selected' : ''}`}
            aria-pressed={comparisonSelected}
            disabled={comparisonDisabled}
            onClick={(event) => {
              event.stopPropagation();
              onToggleComparison(p.id);
            }}
          >
            <span aria-hidden="true">
              {comparisonSelected ? <Icon name="check" size={13}/> : <Icon name="plus" size={13}/>}
            </span>
            {comparisonSelected ? 'Added to comparison' : 'Add to comparison'}
          </button>

          <div style={{
            display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
            marginTop: 14, fontSize: 13, color: 'var(--ink-2)',
          }}>
            <Icon name="shield" size={14} style={{ color: 'var(--brand)' }}/>
            <span style={{ color: 'var(--ink-3)' }}>Illustrative provider profile</span>
            {isOpen && (
              <>
                <span style={{ color: 'var(--ink-4)' }}>·</span>
                <span style={{ color: 'var(--open)', fontWeight: 600 }}>
                  {searchedAge
                    ? `${relevantSpots} ${searchedAge} ${relevantSpots === 1 ? 'spot' : 'spots'} open`
                    : `${p.spots} ${p.spots === 1 ? 'spot' : 'spots'} open`}
                </span>
              </>
            )}
          </div>

          <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: isOpen ? '1fr auto' : '1fr', gap: 10 }}>
            {isOpen ? (
              <>
                <Button
                  variant="primary" full size="md"
                  onClick={(e) => { e.stopPropagation(); onBook(p.id); }}
                  icon="calendar"
                >
                  Schedule
                </Button>
                <Button
                  variant="secondary" size="md"
                  onClick={(e) => { e.stopPropagation(); go('profile', { providerId: p.id }); }}
                  iconRight="chev-right"
                >
                  Profile
                </Button>
              </>
            ) : (
              <Button
                variant="secondary" full size="md"
                onClick={(e) => {
                  e.stopPropagation();
                  onWaitlist(p.id, searchedAge);
                }}
              >
                Join Waitlist
              </Button>
            )}
          </div>
        </div>
      </Card>
    </Reveal>
  );
};

export const ResultsScreen = ({ go, query, onBook, onWaitlist }) => {
  const [subsidyOnly, setSubsidyOnly] = React.useState(false);
  const [zip, setZip] = React.useState(query.zip || '');
  const [childrenAges, setChildrenAges] = React.useState(query.childrenAges || ['Young Toddler']);
  const [schedule, setSchedule] = React.useState(query.schedule || 'Full time');
  const [sort, setSort] = React.useState('Open first');
  const [comparisonIds, setComparisonIds] = React.useState([]);
  const isMobile = useIsMobile();

  const childrenCount = childrenAges.length;
  const setChildAge = (i, val) => setChildrenAges(prev => {
    const next = prev.slice();
    next[i] = val;
    return next;
  });
  const setChildrenCount = (n) => setChildrenAges(prev => {
    if (prev.length === n) return prev;
    if (prev.length < n) return [...prev, ...Array(n - prev.length).fill('Young Toddler')];
    return prev.slice(0, n);
  });

  // Fetch through the API seam. Mock today, Azure Functions tomorrow.
  // Depend on the *committed* query (props), not the local form state, so the
  // search only re-fires when the user clicks Update — not on every keystroke.
  const queryAgeKey = (query.childrenAges || []).join('|');
  const { data: providers, loading, error, reload } = useApi(
    () => api.providers.search({
      zip: query.zip,
      childrenAges: query.childrenAges,
      schedule: query.schedule,
      subsidyOnly,
    }),
    [query.zip, queryAgeKey, query.schedule, subsidyOnly],
  );

  const list = React.useMemo(() => {
    const rows = (providers || []).slice();
    rows.sort((a, b) => {
      const openFirst =
        (hasOpeningsForAges(a, query.childrenAges) ? 0 : 1) -
        (hasOpeningsForAges(b, query.childrenAges) ? 0 : 1);
      if (openFirst !== 0) return openFirst;
      if (sort === 'Distance') return parseFloat(a.distance) - parseFloat(b.distance);
      if (sort === 'Rating') return b.rating - a.rating;
      return b.spots - a.spots;
    });
    return rows;
  }, [providers, sort, queryAgeKey]);

  const openCount = list.filter(p => hasOpeningsForAges(p, query.childrenAges)).length;
  const waitCount = list.length - openCount;

  const updateSearch = () => go('results', { zip, childrenAges, schedule });
  const toggleComparison = (id) => {
    setComparisonIds((current) => {
      if (current.includes(id)) return current.filter((providerId) => providerId !== id);
      if (current.length >= 3) return current;
      return [...current, id];
    });
  };

  return (
    <div className="route-in" style={{ paddingBottom: 64 }}>
      {/* Search bar (compact) */}
      <section style={{ paddingTop: 24, paddingBottom: 18, borderBottom: '1px solid var(--line)' }}>
        <div className="container">
          <div className="results-search" style={{
            display: 'flex', flexDirection: 'column', gap: 6,
            background: 'rgba(255,255,255,0.86)',
            border: '1px solid var(--line-2)',
            borderRadius: 18, padding: 8,
            boxShadow: 'var(--shadow-md)',
            backdropFilter: 'blur(10px)',
          }}>
            <div className="results-search-row" style={{
              display: 'grid',
              gridTemplateColumns: '1.1fr 1fr 1fr auto',
              gap: 0, alignItems: 'center',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
                <Icon name="pin" size={16} style={{ color: 'var(--ink-3)' }}/>
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
                  inputMode="numeric"
                  placeholder="ZIP"
                  style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 15, fontWeight: 500, minWidth: 0 }}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderLeft: '1px solid var(--line)' }}>
                <Icon name="user" size={16} style={{ color: 'var(--ink-3)' }}/>
                <select
                  value={childrenCount}
                  onChange={(e) => setChildrenCount(Number(e.target.value))}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, fontWeight: 500, flex: 1, appearance: 'none' }}
                >
                  {[1, 2, 3, 4, 5].map(n => (
                    <option key={n} value={n}>{n} {n === 1 ? 'child' : 'children'}</option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderLeft: '1px solid var(--line)' }}>
                <Icon name="calendar" size={16} style={{ color: 'var(--ink-3)' }}/>
                <select
                  value={schedule}
                  onChange={(e) => setSchedule(e.target.value)}
                  style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 15, fontWeight: 500, flex: 1, appearance: 'none' }}
                >
                  {SCHEDULES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <Button variant="primary" size="md" icon="search" onClick={updateSearch} style={{ borderRadius: 12 }}>{isMobile ? 'Search' : 'Update'}</Button>
            </div>

            <div className="results-search-ages" style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(childrenCount, 3)}, minmax(0, 1fr))`,
              gap: 0,
              borderTop: '1px solid var(--line)',
              paddingTop: 4,
            }}>
              {Array.from({ length: childrenCount }).map((_, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderLeft: i === 0 ? 'none' : '1px solid var(--line)',
                  minWidth: 0,
                }}>
                  <Icon name="heart" size={16} style={{ color: 'var(--ink-3)' }}/>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                    {childrenCount === 1 ? 'Age' : `Child ${i + 1}`}
                  </span>
                  <select
                    value={childrenAges[i] || 'Young Toddler'}
                    onChange={(e) => setChildAge(i, e.target.value)}
                    style={{
                      border: 'none', outline: 'none', background: 'transparent',
                      fontSize: 15, fontWeight: 500, flex: 1, appearance: 'none', minWidth: 0,
                    }}
                  >
                    {AGE_GROUPS.map(g => (
                      <option key={g} value={g}>{g} ({AGE_GROUP_RANGES[g]})</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '24px 0' }}>
        <div className="container">
          {/* Subheader */}
          <Reveal>
            <div className="results-controls" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap', marginBottom: 18,
            }}>
              <div>
                <h2 style={{
                  fontSize: 'clamp(20px, 3vw, 24px)', fontWeight: 700, color: 'var(--ink)',
                  letterSpacing: '-0.02em',
                }}>
                  {loading
                    ? <Skeleton height={26} width={260}/>
                    : `${list.length} centers ${zip ? `near ${zip}` : 'matching your search'}`}
                </h2>
                <p style={{ color: 'var(--ink-3)', fontSize: 14, marginTop: 4 }}>
                  Open spots shown first ·{' '}
                  <span style={{ color: 'var(--open)', fontWeight: 600 }}>{openCount} open</span>
                  {' '}·{' '}
                  <span style={{ color: 'var(--wait)', fontWeight: 600 }}>{waitCount} waitlist</span>
                </p>
              </div>
              <div className="sort-row" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: 13, color: 'var(--ink-3)', marginRight: 4 }}>Sort:</span>
                <Pill active={sort === 'Open first'}  onClick={() => setSort('Open first')}>Open first</Pill>
                <Pill active={sort === 'Distance'}    onClick={() => setSort('Distance')}>Distance</Pill>
                <Pill active={sort === 'Rating'}      onClick={() => setSort('Rating')}>Rating</Pill>
              </div>
            </div>
          </Reveal>

          {/* Filter row */}
          <div className="filter-row" style={{
            display: 'flex', alignItems: 'center', gap: 10,
            flexWrap: 'wrap', marginBottom: 22,
            paddingBottom: 18, borderBottom: '1px solid var(--line)',
          }}>
            <span style={{
              fontSize: 13, fontWeight: 600, color: 'var(--ink-3)',
              display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 4,
            }}>
              <Icon name="sliders" size={14}/>
              Filters
            </span>
            <Toggle value={subsidyOnly} onChange={setSubsidyOnly} label="Subsidy accepted"/>
            <Pill>Distance: 5 mi</Pill>
          </div>

          {/* Grid */}
          {error ? (
            <ErrorState error={error} onRetry={reload}/>
          ) : (
            <div style={{
              display: 'grid', gap: 20,
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            }}>
              {loading
                ? Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i}/>)
                : list.map((p, i) => (
                    <ResultCard
                      key={p.id}
                      p={p}
                      go={go}
                      onBook={onBook}
                      onWaitlist={onWaitlist}
                      delay={i * 70}
                      searchedAges={query.childrenAges}
                      comparisonSelected={comparisonIds.includes(p.id)}
                      comparisonDisabled={comparisonIds.length >= 3 && !comparisonIds.includes(p.id)}
                      onToggleComparison={toggleComparison}
                    />
                  ))}
            </div>
          )}

          {!loading && !error && list.length === 0 && (
            <div style={{
              padding: '64px 24px', textAlign: 'center',
              background: 'var(--surface)', border: '1px dashed var(--line-2)',
              borderRadius: 'var(--r-md)',
            }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)', marginBottom: 6 }}>No centers match these filters.</h3>
              <p style={{ color: 'var(--ink-3)' }}>Try widening your distance, or removing the subsidy or age filter.</p>
            </div>
          )}
        </div>
      </section>
      {comparisonIds.length > 0 && (
        <div className="comparison-tray" role="region" aria-label="Provider comparison">
          <div>
            <strong>{comparisonIds.length} of 3 selected</strong>
            <span>
              {comparisonIds.length < 2
                ? 'Choose one more provider to compare'
                : 'Compare current openings, hours, schedules, and subsidy information'}
            </span>
          </div>
          <div>
            <Button variant="ghost" size="sm" onClick={() => setComparisonIds([])}>Clear</Button>
            <Button
              size="sm"
              disabled={comparisonIds.length < 2}
              onClick={() => go('compare', { providerIds: comparisonIds })}
            >
              Compare {comparisonIds.length}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
